import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

/**
 * LEGEND CONFIGURATIONS
 * Each entry maps to a viewMode and defines the vertical gradient bar.
 * stops: array of { value, color } from HIGH → LOW (top to bottom).
 * These match the getXxxColor() functions in MapView.js exactly.
 */
const LEGEND_CONFIGS = {
    temperature: {
        unit: '°F',
        stops: [
            { value: 120, color: '#991B1B' },
            { value: 100, color: '#DC2626' },
            { value: 90,  color: '#EA580C' },
            { value: 70,  color: '#F59E0B' },
            { value: 60,  color: '#10B981' },
            { value: 50,  color: '#06B6D4' },
            { value: 40,  color: '#3B82F6' },
            { value: 30,  color: '#2563EB' },
            { value: 10,  color: '#1E40AF' },
            { value: 0,   color: '#1E3A8A' },
            { value: -10, color: '#172554' },
            { value: -40, color: '#0c1a3d' },
        ],
    },
    wind: {
        unit: 'mph',
        stops: [
            { value: 90,  color: '#2a0001' },
            { value: 80,  color: '#72222f' },
            { value: 70,  color: '#b02f6b' },
            { value: 60,  color: '#ce4c58' },
            { value: 50,  color: '#d88e36' },
            { value: 45,  color: '#e9bc26' },
            { value: 40,  color: '#dcdb31' },
            { value: 35,  color: '#bade3f' },
            { value: 30,  color: '#83cc4b' },
            { value: 25,  color: '#31b94d' },
            { value: 20,  color: '#41adb5' },
            { value: 15,  color: '#478ec7' },
            { value: 10,  color: '#476fbb' },
            { value: 5,   color: '#5651a2' },
            { value: 0,   color: '#605e71' },
        ],
    },
    wind_gust: {
        unit: 'mph',
        stops: [
            { value: 100, color: '#1a0000' },
            { value: 90,  color: '#2a0001' },
            { value: 80,  color: '#72222f' },
            { value: 70,  color: '#b02f6b' },
            { value: 60,  color: '#ce4c58' },
            { value: 50,  color: '#d88e36' },
            { value: 45,  color: '#e9bc26' },
            { value: 40,  color: '#dcdb31' },
            { value: 35,  color: '#bade3f' },
            { value: 30,  color: '#83cc4b' },
            { value: 25,  color: '#31b94d' },
            { value: 20,  color: '#41adb5' },
            { value: 15,  color: '#478ec7' },
            { value: 10,  color: '#476fbb' },
            { value: 5,   color: '#5651a2' },
            { value: 0,   color: '#605e71' },
        ],
    },
    irradiance: {
        unit: 'W/m²',
        stops: [
            { value: 1000, color: '#FEF9C3' },
            { value: 800,  color: '#FEF08A' },
            { value: 600,  color: '#FDE047' },
            { value: 400,  color: '#F59E0B' },
            { value: 200,  color: '#D97706' },
            { value: 50,   color: '#92400E' },
            { value: 0,    color: '#1E3A8A' },
        ],
    },
    thunderstorm: {
        unit: 'J/kg',
        stops: [
            { value: 5000, color: '#270104' },
            { value: 4000, color: '#571330' },
            { value: 3000, color: '#7a1945' },
            { value: 2500, color: '#9e225e' },
            { value: 2000, color: '#db5858' },
            { value: 1800, color: '#d46f2d' },
            { value: 1600, color: '#eab308' },
            { value: 1400, color: '#fde047' },
            { value: 1200, color: '#b2d94a' },
            { value: 1000, color: '#65b55e' },
            { value: 800,  color: '#4cb490' },
            { value: 600,  color: '#3daeb5' },
            { value: 400,  color: '#425da6' },
            { value: 200,  color: '#5b57a3' },
            { value: 0,    color: '#8c8c8c' },
        ],
    },
    score: {
        unit: '',
        stops: [
            { value: 'Gold',     color: '#F59E0B' },
            { value: 'Silver',   color: '#60A5FA' },
            { value: 'Moderate', color: '#FB923C' },
            { value: 'Poor',     color: '#EF4444' },
            { value: 'N/A',      color: '#6B7280' },
        ],
    },
};

const MapLegend = ({ viewMode }) => {
    const map = useMap();

    useEffect(() => {
        const config = LEGEND_CONFIGS[viewMode];
        if (!config) return;

        const legend = L.control({ position: 'bottomleft' });

        legend.onAdd = () => {
            const container = L.DomUtil.create('div', 'weather-legend');
            L.DomEvent.disableClickPropagation(container);
            L.DomEvent.disableScrollPropagation(container);

            const stops = config.stops;
            const barHeight = Math.min(320, stops.length * 28);

            // Build CSS gradient string (top = high value, bottom = low value)
            const gradientStops = stops.map((s, i) => {
                const pct = (i / (stops.length - 1)) * 100;
                return `${s.color} ${pct}%`;
            }).join(', ');

            // Build tick marks HTML — labels on the RIGHT side of the bar
            const ticksHtml = stops.map((s, i) => {
                const pct = (i / (stops.length - 1)) * 100;
                return `
                    <div style="
                        position: absolute;
                        top: ${pct}%;
                        left: 0;
                        transform: translateY(-50%);
                        display: flex;
                        align-items: center;
                        height: 16px;
                    ">
                        <div style="
                            width: 5px;
                            height: 1px;
                            background: rgba(255,255,255,0.4);
                        "></div>
                        <span style="
                            font-size: 9px;
                            font-weight: 600;
                            color: #cbd5e1;
                            white-space: nowrap;
                            margin-left: 4px;
                            font-family: 'Inter', system-ui, sans-serif;
                            line-height: 1;
                        ">${s.value}</span>
                    </div>
                `;
            }).join('');

            container.innerHTML = `
                <div style="
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    background: rgba(15, 23, 42, 0.82);
                    backdrop-filter: blur(12px);
                    -webkit-backdrop-filter: blur(12px);
                    border-radius: 10px;
                    border: 1px solid rgba(255,255,255,0.1);
                    padding: 10px 10px 12px 10px;
                    box-shadow: 0 4px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.04) inset;
                ">
                    <!-- Unit label -->
                    <div style="
                        font-size: 9px;
                        font-weight: 700;
                        color: #94a3b8;
                        margin-bottom: 6px;
                        letter-spacing: 0.06em;
                        text-transform: uppercase;
                        font-family: 'Inter', system-ui, sans-serif;
                    ">${config.unit}</div>

                    <div style="
                        position: relative;
                        display: flex;
                        align-items: stretch;
                        height: ${barHeight}px;
                    ">
                        <!-- Gradient bar -->
                        <div style="
                            width: 14px;
                            height: 100%;
                            border-radius: 7px;
                            background: linear-gradient(to bottom, ${gradientStops});
                            box-shadow:
                                0 0 6px rgba(0,0,0,0.3),
                                inset 0 1px 0 rgba(255,255,255,0.15);
                            border: 1px solid rgba(255,255,255,0.12);
                            flex-shrink: 0;
                        "></div>

                        <!-- Tick labels on the right -->
                        <div style="
                            position: relative;
                            width: 40px;
                            height: 100%;
                        ">
                            ${ticksHtml}
                        </div>
                    </div>
                </div>
            `;
            return container;
        };

        legend.addTo(map);
        return () => legend.remove();
    }, [map, viewMode]);

    return null;
};

export default MapLegend;
